import { useSyncExternalStore } from 'react';
import { useUser } from 'services/User';

let defaultScopeApplied = false;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => defaultScopeApplied;
const getServerSnapshot = () => false;

/** Called by the Layout effect once it has applied the default org scope, so an empty org filter afterwards means Personal. */
export const markOrgDefaultScopeApplied = () => {
    if (defaultScopeApplied) {
        return;
    }

    defaultScopeApplied = true;
    listeners.forEach((listener) => listener());
};

/** A new session starts with the default org scope unresolved again. */
export const resetOrgDefaultScope = () => {
    if (!defaultScopeApplied) {
        return;
    }

    defaultScopeApplied = false;
    listeners.forEach((listener) => listener());
};

/**
 * True while an org user is loaded but the Layout effect has not applied the default org scope yet.
 *
 * SWR skips the request when a key is null, so callers return a null key while this is true
 * to avoid a personal-scope (customer_id) request that is immediately replaced by an org-scope one.
 * Once the default is applied, an empty org filter is an explicit Personal choice and is never pending.
 * An org user is anyone with organization.organization_id, the same condition the Layout effect uses to fill the filter.
 * A personal-only user (no organization.organization_id) is never pending.
 */
export const useOrgScopePending = (organizationIds?: string[]): boolean => {
    const { user } = useUser();
    const isDefaultScopeApplied = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const isOrgUser = Boolean(user?.organization?.organization_id);

    return isOrgUser && !isDefaultScopeApplied && !(Array.isArray(organizationIds) && organizationIds.length > 0);
};

/** A null SWR key reports isLoading false, so while the scope is pending the hook reports loading instead of an empty state. */
export const withOrgScopeLoading = <T extends { isLoading: boolean }>(response: T, orgScopePending: boolean): T =>
    orgScopePending ? { ...response, isLoading: true } : response;
